import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCanonicalEvents } from '../hooks/useCanonicalEvents';
import { securityApi } from '../services/securityApi';
import type {
  CanonicalEventFilters,
  CanonicalEventListItem,
  OperatorEventState,
  SeverityToken,
} from '../types/security';
import { EventsTable } from './EventsTable';
import { MultiSkillFilter } from './MultiSkillFilter';
import { EmptyState, ErrorState, LoadingSkeleton } from './LoadingSkeleton';

const ALL_OPERATOR_STATES: OperatorEventState[] = [
  'active', 'acknowledged', 'snoozed', 'dismissed_fp',
];
const DEFAULT_OPERATOR_STATE_FILTER: OperatorEventState[] = [
  'active', 'acknowledged',
];
const OPERATOR_STATE_PARAM = 'operator_state';

const SEVERITY_TOKENS: SeverityToken[] = [
  'critical',
  'high',
  'medium',
  'low',
  'informational',
];

const KEV_EXPLOITED = 'Exploited';

/** Charter-floor routing-eligibility per parked decision #1. */
function isRoutingEligible(item: CanonicalEventListItem): boolean {
  if (item.latest_status === KEV_EXPLOITED) return true;
  const sev = item.max_severity ?? 0;
  return sev >= 7;
}

function severityTokenFromScore(score: number | null): SeverityToken {
  if (score == null) return 'informational';
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  if (score > 0) return 'low';
  return 'informational';
}

export function ThreatsTab(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const [cveSearch, setCveSearch] = useState('');
  const [minSeverity, setMinSeverity] = useState<number | undefined>(undefined);
  const [latestStatusCsv, setLatestStatusCsv] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // WQ-298 Finding B: a grouped REM- deep-links here as ?package=eco:pkg.
  // Filter events to that package (affected_target ILIKE) so "View source"
  // lands filtered AND a "Send all for package" affordance is meaningful.
  const packageFilter = searchParams.get('package');

  // WQ-298 D4: multi-select for package-grouped bulk send.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sendBusy, setSendBusy] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // WQ-283 (DEF-023): operator_state filter selection synced with URL
  // search params for deep-link sharing (extends WQ-G deep-link pattern).
  const operatorStateFilter: OperatorEventState[] = useMemo(() => {
    const raw = searchParams.get(OPERATOR_STATE_PARAM);
    if (raw === null) return DEFAULT_OPERATOR_STATE_FILTER;
    if (raw === '') return [];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is OperatorEventState =>
        (ALL_OPERATOR_STATES as string[]).includes(s),
      );
  }, [searchParams]);

  const toggleOperatorState = (state: OperatorEventState) => {
    const next = operatorStateFilter.includes(state)
      ? operatorStateFilter.filter((s) => s !== state)
      : [...operatorStateFilter, state];
    setSearchParams((prev) => {
      if (next.length === ALL_OPERATOR_STATES.length) {
        prev.delete(OPERATOR_STATE_PARAM);
      } else {
        prev.set(OPERATOR_STATE_PARAM, next.join(','));
      }
      return prev;
    });
    setOffset(0);
  };

  // WQ-286 Item 4: operator_state filter is now server-side. Always send the
  // param when chips are selected to preserve frontend↔backend filter
  // discipline. When operator has deselected ALL chips, short-circuit the
  // fetch entirely (empty-state UI; sending no param would mean "all states"
  // which is the opposite of the operator's intent).
  const allChipsDeselected = operatorStateFilter.length === 0;
  const filters: CanonicalEventFilters = useMemo(
    () => ({
      cve_id: cveSearch.trim() || undefined,
      min_max_severity: minSeverity,
      latest_status: latestStatusCsv.trim() || undefined,
      affected_target: packageFilter || undefined, // WQ-298 Finding B
      operator_state: allChipsDeselected ? undefined : operatorStateFilter.join(','),
      limit,
      offset,
    }),
    [cveSearch, minSeverity, latestStatusCsv, packageFilter, operatorStateFilter, allChipsDeselected, offset],
  );

  const { data, isLoading, error, refetch } = useCanonicalEvents(
    filters,
    // Skip fetch when operator deselected all chips — preserves bandwidth +
    // avoids the ambiguous "no param" interpretation. Hook short-circuits.
    { skip: allChipsDeselected },
  );

  // WQ-286 Item 4: server-side filter applied; client-side filter removed.
  // `visibleItems` now equals data.items (no post-fetch filtering).
  const visibleItems = data?.items ?? [];

  const openDetail = (id: string) => {
    setSearchParams((prev) => {
      prev.set('canonical_event_id', id);
      return prev;
    });
  };

  // Clear selection + send status whenever the result set changes.
  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    clearSelection();
    setSendStatus(null);
    setSendError(null);
  }, [filterKey]);

  const sendIds = async (ids: string[], label: string) => {
    if (ids.length === 0) return;
    setSendBusy(true);
    setSendError(null);
    setSendStatus(`Sending ${ids.length} ${label} to orchestrator…`);
    try {
      const resp = await securityApi.sendGrouped(authFetch, { canonical_event_ids: ids });
      const pkgs = resp.groups
        .filter((g) => g.package)
        .map((g) => `${g.ecosystem}:${g.package}`);
      setSendStatus(
        `Enqueued ${resp.rows_enqueued} row(s)` +
          (pkgs.length ? ` — packages: ${pkgs.join(', ')}` : ''),
      );
      clearSelection();
      refetch();
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
      setSendStatus(null);
    } finally {
      setSendBusy(false);
    }
  };

  const sendSelected = () => sendIds(Array.from(selectedIds), 'selected');
  const sendAllForPackage = () =>
    sendIds(visibleItems.map((i) => i.id), 'events for this package');

  const clearPackageFilter = () => {
    setSearchParams((prev) => {
      prev.delete('package');
      return prev;
    });
    setOffset(0);
  };

  const selection = { selectedIds, onToggle: toggleSelect };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '1rem',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>CVE ID</span>
          <input
            type="search"
            value={cveSearch}
            onChange={(e) => {
              setCveSearch(e.target.value);
              setOffset(0);
            }}
            placeholder="CVE-2026-31431"
            aria-label="Filter by CVE ID"
            style={{ padding: '0.25rem 0.5rem' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Min severity (0-10)</span>
          <input
            type="number"
            min={0}
            max={10}
            step={0.1}
            value={minSeverity ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setMinSeverity(v === '' ? undefined : Number(v));
              setOffset(0);
            }}
            aria-label="Minimum severity"
            style={{ padding: '0.25rem 0.5rem', width: '6rem' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Status (CSV)</span>
          <input
            type="text"
            value={latestStatusCsv}
            onChange={(e) => {
              setLatestStatusCsv(e.target.value);
              setOffset(0);
            }}
            placeholder="Analyzed,Modified,Exploited"
            aria-label="Filter by status"
            style={{ padding: '0.25rem 0.5rem' }}
          />
        </label>
        <MultiSkillFilter />
      </div>

      <div
        role="group"
        aria-label="Filter by operator state"
        style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}
      >
        <span style={{ fontSize: '0.75rem', alignSelf: 'center', color: 'var(--text-secondary)' }}>
          Operator state:
        </span>
        {ALL_OPERATOR_STATES.map((s) => {
          const active = operatorStateFilter.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleOperatorState(s)}
              aria-pressed={active}
              className={`btn ${active ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}
            >
              {s}
            </button>
          );
        })}
      </div>

      {/* WQ-298 D4: grouped-send toolbar (package banner + bulk send). */}
      {packageFilter && (
        <div
          role="status"
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-tertiary, #f3f4f6)',
            borderRadius: 6,
            fontSize: '0.8125rem',
          }}
        >
          <span>
            Filtered to package <strong style={{ fontFamily: 'monospace' }}>{packageFilter}</strong>
          </span>
          <button
            className="btn btn-primary"
            onClick={sendAllForPackage}
            disabled={sendBusy || visibleItems.length === 0}
            style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}
          >
            Send all for package ({visibleItems.length})
          </button>
          <button
            className="btn btn-secondary"
            onClick={clearPackageFilter}
            style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}
          >
            Clear package filter
          </button>
        </div>
      )}
      {(selectedIds.size > 0 || sendStatus || sendError) && (
        <div
          role="status"
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: '0.75rem',
            fontSize: '0.8125rem',
          }}
        >
          {selectedIds.size > 0 && (
            <>
              <span>{selectedIds.size} selected</span>
              <button
                className="btn btn-primary"
                onClick={sendSelected}
                disabled={sendBusy}
                style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}
              >
                Send selected to orchestrator
              </button>
              <button
                className="btn btn-secondary"
                onClick={clearSelection}
                disabled={sendBusy}
                style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}
              >
                Clear selection
              </button>
            </>
          )}
          {sendStatus && <span style={{ color: 'var(--text-secondary)' }}>{sendStatus}</span>}
          {sendError && <span style={{ color: 'var(--danger, #dc2626)' }}>{sendError}</span>}
        </div>
      )}

      {isLoading && !data && <LoadingSkeleton />}
      {error && <ErrorState error={error} onRetry={refetch} />}
      {allChipsDeselected && (
        <EmptyState
          message={
            'No operator-state chips selected. Toggle at least one chip ' +
            '(active / acknowledged / snoozed / dismissed_fp) above to fetch events.'
          }
        />
      )}
      {!allChipsDeselected && !isLoading && data && visibleItems.length === 0 && (
        <EmptyState
          message={
            'No canonical CVE events match current filters. Cyber-agent observes ' +
            'intel cycles every Monday UTC. CVE-2026-31431 (Copy Fail kernel ' +
            'transition) is the Day-1 trigger.'
          }
        />
      )}

      {data && visibleItems.length > 0 && (
        <>
          <EventsTable items={visibleItems} onSelect={openDetail} selection={selection} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.75rem',
              fontSize: '0.875rem',
            }}
          >
            <div>
              Showing {visibleItems.length} of {data.total} events
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={data.items.length < limit || offset + limit >= data.total}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { SEVERITY_TOKENS, KEV_EXPLOITED, isRoutingEligible, severityTokenFromScore };
